import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import { cn } from '@apps/web/lib/utils';

/*
 * Skeleton kit. Admin pages standardize on skeletons over spinners:
 * the loading state keeps the final layout's shape so content does
 * not jump when data arrives.
 *
 * Round 4 shimmer: a real background-position sweep, not a pulse.
 * The gradient (canvas → sunken → canvas) is sized at 200% width and
 * animated from `100% 0` → `0% 0` over 1.4s ease-in-out infinite via
 * the `.admin-skeleton` keyframe declared in admin_theme.css. Under
 * prefers-reduced-motion the theme layer disables the keyframe and
 * the tint falls back to a static sunken fill.
 */

type AdminSkeletonShape = 'text' | 'card' | 'circle';

const SHAPE_RADIUS: Record<AdminSkeletonShape, string> = {
    text: 'rounded-admin-sm',
    card: 'rounded-admin-md',
    circle: 'rounded-full',
};

// Sweep canvas → purple-tinted mid-stop → canvas so the loading tint
// reads as a subtle well against the surface with a whisper of the
// signature hue. The mid-stop is a hair darker than the canvas
// (#faf9fb) and carries the same slight purple cast as the ink axis
// so the shimmer band is visible without pulling toward neutral grey.
const SHIMMER_BACKGROUND_IMAGE =
    'linear-gradient(90deg, var(--admin-canvas) 0%, #f0edf5 50%, var(--admin-canvas) 100%)';

type AdminSkeletonProps = React.ComponentProps<'div'> & {
    readonly shape?: AdminSkeletonShape;
};

export function AdminSkeleton({
    className,
    shape = 'text',
    style,
    ...props
}: AdminSkeletonProps) {
    return (
        <div
            data-slot="admin-skeleton"
            data-shape={shape}
            aria-hidden="true"
            className={cn('admin-skeleton h-4', SHAPE_RADIUS[shape], className)}
            style={{
                backgroundImage: SHIMMER_BACKGROUND_IMAGE,
                backgroundSize: '200% 100%',
                backgroundPosition: '100% 0',
                backgroundColor: 'var(--admin-sunken)',
                ...style,
            }}
            {...props}
        />
    );
}

// Deterministic width cycle so skeleton rows read as varied content
// without random layout between renders.
const SKELETON_CELL_WIDTHS = ['w-32', 'w-20', 'w-40', 'w-24', 'w-16'] as const;

export function AdminTableSkeleton({
    columns,
    rows = 8,
    className,
}: {
    readonly columns: ReadonlyArray<string>;
    readonly rows?: number;
    readonly className?: string;
}) {
    return (
        <div role="status" className={className}>
            <span className="sr-only">Loading…</span>
            <AdminTable aria-hidden={true}>
                <AdminTableHeader>
                    <tr>
                        {columns.map((column) => (
                            <AdminTableHeadCell key={column}>
                                {column}
                            </AdminTableHeadCell>
                        ))}
                    </tr>
                </AdminTableHeader>
                <AdminTableBody>
                    {Array.from({ length: rows }, (_, rowIndex) => (
                        <AdminTableRow
                            key={rowIndex}
                            className="hover:bg-transparent"
                        >
                            {columns.map((column, columnIndex) => (
                                <AdminTableCell key={column}>
                                    <AdminSkeleton
                                        shape="text"
                                        className={
                                            SKELETON_CELL_WIDTHS[
                                                (rowIndex + columnIndex) %
                                                    SKELETON_CELL_WIDTHS.length
                                            ]
                                        }
                                    />
                                </AdminTableCell>
                            ))}
                        </AdminTableRow>
                    ))}
                </AdminTableBody>
            </AdminTable>
        </div>
    );
}
